import { useEffect, useState } from "react";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { EmployeeDashboardUpdateDto } from "../../../types/dtos/dashboards";
import { Accordion } from "../../common";
import { AccordionItem } from "../../common/Accordion";
import EmployeeUpdateHeader from "./EmployeeUpdateHeader";
import EmployeeUpdateBody from "./EmployeeUpdateBody";

interface EmployeeJourneyUpdatesProps {
  useShorterText: boolean;
  /** Any updates relating to open collab docs or dual prep flows */
  updates: EmployeeDashboardUpdateDto[];
  /** A function which changes the user's current journey and refreshes the journey widget */
  changeJourney: (journeyRef: string) => void;
}

const getUpdateKey = (update: EmployeeDashboardUpdateDto) =>
  update.journey?.journeyReference ??
  (update.task
    ? `${update.updateType}-${update.task.taskTypeId}`
    : update.updateType);

const ACCORDION_TRIGGER_CLASS_NAMES = "grow text-left";
const ACCORDION_TRIGGER_BG_CLASS_NAMES =
  "cursor-pointer rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent";

function EmployeeJourneyUpdates({
  useShorterText,
  updates,
  changeJourney,
}: EmployeeJourneyUpdatesProps) {
  const { t } = useTranslation();
  const accordionItemKeys = updates.map(getUpdateKey);
  const [activeItemId, setActiveItemId] = useState<string>(
    () => accordionItemKeys[0] ?? "",
  );

  useEffect(() => {
    setActiveItemId(
      updates.length > 0 ? getUpdateKey(updates[0]) : "",
    );
  }, [updates]);

  if (!updates?.length) return null;

  const onAccordionActiveSectionChange = (sectionId: string | string[]) => {
    setActiveItemId(sectionId as string);
  };

  return (
    <section
      id="journey-updates-container"
      aria-labelledby="journey-updates-heading"
    >
      <h2
        id="journey-updates-heading"
        className="journey-updates-heading light-heading !mb-3 !text-white text-balance"
      >
        {t("Journey.Updates.Title", "What's next…")}
      </h2>
      <Accordion
        allowMultipleActiveSections={false}
        activeSection={activeItemId}
        onActiveSectionChange={onAccordionActiveSectionChange}
        allowCollapseOpenItem={false}
        allItemKeys={accordionItemKeys}
      >
        {updates.map((update) => {
          const itemKey = getUpdateKey(update);
          const itemIsExpanded = itemKey === activeItemId;
          return (
            <AccordionItem
              key={itemKey}
              itemValue={itemKey}
              showChevronIcons={false}
              triggerClassNames={ACCORDION_TRIGGER_CLASS_NAMES}
              triggerBackgroundColourClassNames={
                ACCORDION_TRIGGER_BG_CLASS_NAMES
              }
              trigger={
                <EmployeeUpdateHeader
                  useShorterText={useShorterText}
                  update={update}
                  isExpanded={itemIsExpanded}
                />
              }
              content={
                <EmployeeUpdateBody
                  update={update}
                  changeJourney={changeJourney}
                />
              }
              classNames={cx(
                "mb-2 rounded-lg p-3 transition-colors duration-150 last:mb-0 motion-reduce:transition-none",
                itemIsExpanded
                  ? "bg-white/20"
                  : "bg-white/10 hover:bg-white/15",
              )}
            />
          );
        })}
      </Accordion>
    </section>
  );
}

export default EmployeeJourneyUpdates;
